import { useCallback, useEffect, useRef } from "react";

export function useFileQueueInteractions({
  files,
  selectedIndices,
  primaryIndex,
  selectSingle,
  toggleSelect,
  rangeSelect,
  addFiles,
}) {
  const scrollContainerRef = useRef(null);
  const prevPrimaryIndexRef = useRef(null);
  const rangeAnchorIndexRef = useRef(null);

  const handleFileClick = useCallback(
    (index, event) => {
      if (event.shiftKey && primaryIndex >= 0) {
        const anchorIndex =
          rangeAnchorIndexRef.current === null
            ? primaryIndex
            : rangeAnchorIndexRef.current;
        rangeAnchorIndexRef.current = anchorIndex;
        rangeSelect(anchorIndex, index);
      } else if (event.ctrlKey || event.metaKey) {
        rangeAnchorIndexRef.current = null;
        toggleSelect(index);
      } else {
        rangeAnchorIndexRef.current = null;
        selectSingle(index);
      }
    },
    [primaryIndex, rangeSelect, toggleSelect, selectSingle],
  );

  const selectByKeyboard = useCallback(
    (index, withRange = false) => {
      if (index < 0 || index >= files.length) return;

      if (withRange && primaryIndex >= 0) {
        const anchorIndex =
          rangeAnchorIndexRef.current === null
            ? primaryIndex
            : rangeAnchorIndexRef.current;
        rangeAnchorIndexRef.current = anchorIndex;
        rangeSelect(anchorIndex, index);
        return;
      }

      rangeAnchorIndexRef.current = null;
      selectSingle(index);
    },
    [files.length, primaryIndex, rangeSelect, selectSingle],
  );

  const handleDragStart = useCallback(
    (event, index) => {
      event.dataTransfer.effectAllowed = "move";

      if (selectedIndices.has(index)) {
        const indices = Array.from(selectedIndices);
        event.dataTransfer.setData("fileIndices", JSON.stringify(indices));
      } else {
        event.dataTransfer.setData("fileIndices", JSON.stringify([index]));
      }
    },
    [selectedIndices],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      addFiles(event.dataTransfer.files);
    },
    [addFiles],
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  useEffect(() => {
    if (primaryIndex < 0 || prevPrimaryIndexRef.current === null) return;

    const itemSelector = `[data-file-index=\"${primaryIndex}\"]`;
    const itemElement = scrollContainerRef.current?.querySelector(itemSelector);

    if (itemElement) {
      itemElement.scrollIntoView({ behavior: "auto", block: "nearest" });
    }

    prevPrimaryIndexRef.current = primaryIndex;
  }, [primaryIndex]);

  useEffect(() => {
    prevPrimaryIndexRef.current = null;
    rangeAnchorIndexRef.current = null;
  }, [files.length]);

  return {
    scrollContainerRef,
    handleFileClick,
    selectByKeyboard,
    handleDragStart,
    handleDrop,
    handleDragOver,
  };
}

export default useFileQueueInteractions;
